import { createAsyncThunk } from "@reduxjs/toolkit";
import { debounce } from "@mui/material";
import { AxiosError } from "axios";

import {
  // getRanksAndPrices,
  getKeepa,
  getProductVariations,
  getAverages,
  getProductInfo,
} from "../../services/api";
import {
  ICreateNoteRequest,
  ICreateProductPurchaseRequest,
  ICreateProductTagRequest,
  IDeleteNoteRequest,
  IDeleteProductTagRequest,
  IGetProductHistoriesRequest,
  IUpdateLookupRequest,
  IUpdateNoteRequest,
  TGetBuyBoxAnalysisParams,
  // TGetOffersParams,
  TGetProductQuickScanRequest,
} from "../../services/apiV2.types";
import {
  createNote,
  createProductPurchase,
  createProductTag,
  deleteNote,
  deleteProductTag,
  fetchNotes,
  // getBuyBoxAnalysis,
  getAmazonRestrictions,
  getLookup,
  getProductHistories,
  getProductQuickScan,
  getProductTags,
  updateLookup,
  updateNote,
} from "../../services/apiV2";
import { UPDATE_DEBOUNCE_DELAY } from "../../constants";
import { RootState } from "../../store/store";
import { TErrorResponse, TOffer } from "./productAnalyzer.types";
import { ProfitCalculator, axiosErrorMatches } from "../../helpers";
import { increaseLookupUsage } from "../user/user.slice";

const ASIN_NOT_FOUND_ERR = "ASIN data not found.";

// Some endpoints respond 200 with an error envelope in the body instead of a non-2xx status.
const isAsinNotFoundResponse = (data: unknown) =>
  !!data && typeof data === "object" && (data as any).ErrorMessage === ASIN_NOT_FOUND_ERR;

export const getProductDetails = createAsyncThunk(
  "productAnalyzer/getProductDetails",
  async (
    { asin, lookupId }: TGetProductQuickScanRequest,
    { getState, rejectWithValue, dispatch },
  ) => {
    const state = getState() as RootState;
    const settings = state.settings;
    try {
      const [quickScanResult, restrictionsResult] = await Promise.allSettled([
        getProductQuickScan({ asin, lookupId }),
        getAmazonRestrictions(asin),
      ]);

      if (quickScanResult.status === "rejected") {
        throw quickScanResult.reason;
      }

      const { data } = quickScanResult.value;

      if (isAsinNotFoundResponse(data)) {
        console.warn(
          `No product data available for ASIN: ${asin}. Reason: ${ASIN_NOT_FOUND_ERR}`,
        );
        return rejectWithValue(data);
      }

      const restrictions =
        restrictionsResult.status === "fulfilled"
          ? restrictionsResult.value.data
          : null;

      if (!lookupId) {
        dispatch(increaseLookupUsage());
      }
      const profitCalculator = data.profitCalculator || {};
      return {
        ...data,
        restrictions,
        profitCalculator: {
          salePrice: profitCalculator.salePrice,
          costPrice: profitCalculator.costPrice,
          fulfilmentType: settings.general.fulfilment,
          fbmCost: settings.productAnalyzer.defaultFbmCost,
          storageTime: settings.general.storageTime,
          minRoi: settings.productAnalyzer.minRoi,
          minProfit: settings.productAnalyzer.minProfit,
          prepFee: settings.general.prepFee,
          inboundShipping: settings.general.inboundShipping,
          inboundPlacementService: settings.general.inboundPlacement,
          miscFee: settings.general.miscFee,
          miscFeePercent: settings.general.miscFeePercent,
          closingFee: data.feeRules?.closingFee || 0,
          storageFee: data.feeRules?.storageFee || 0,
          totalDiscount: 0,
          multipackQuantity: 1,
          quantity: profitCalculator.quantity,
        },
      };
    } catch (e) {
       console.error(e);
      // const error = e as AxiosError<TErrorResponse>;
      // if (error.status === 404) {
      //   enqueueSnackbar(Notifications.ASIN_NOT_FOUND, {
      //     variant: "error",
      //     preventDuplicate: true,
      //   });
      // } else {
      //   enqueueSnackbar(
      //     error.response?.data?.ErrorMessage ?? INTERNAL_SERVER_ERROR,
      //     { variant: "error" },
      //   );
      // }
      return rejectWithValue(e);
    }
  },
);

export const getKeepaThunk = createAsyncThunk(
  "productAnalyzer/getKeepaValue",
  async (
    {
      asin,
      country,
      day,
    }: {
      asin: string;
      country: string;
      day: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await getKeepa(asin, country, day);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = URL.createObjectURL(blob);
      return url;
    } catch (error) {
      console.error(error);
      // enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: "error" });
      return rejectWithValue(error);
    }
  },
);

export const getNotesThunk = createAsyncThunk(
  "productAnalyzer/getNotesThunk",
  async (asin: string, { rejectWithValue }) => {
    try {
      const response = await fetchNotes(asin);
      return response.data;
    } catch (error) {
      console.error(error);
      // enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: "error" });
      return rejectWithValue(error);
    }
  },
);

export const deleteNoteThunk = createAsyncThunk(
  "productAnalyzer/deleteNoteThunk",
  async (req: IDeleteNoteRequest, { rejectWithValue }) => {
    try {
      await deleteNote(req);
      return req.id;
    } catch (error) {
      console.error(error);
      // enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: "error" });
      return rejectWithValue(error);
    }
  },
);

export const createNoteThunk = createAsyncThunk(
  "productAnalyzer/createNoteThunk",
  async (req: ICreateNoteRequest, { rejectWithValue }) => {
    try {
      const response = await createNote(req);
      return response.data;
    } catch (error) {
      console.error(error);
      // enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: "error" });
      return rejectWithValue(error);
    }
  },
);

export const updateNoteThunk = createAsyncThunk(
  "productAnalyzer/updateNoteThunk",
  async (req: IUpdateNoteRequest, { rejectWithValue }) => {
    try {
      const response = await updateNote(req);
      return { id: req.id, ...response.data };
    } catch (error) {
      console.error(error);
      // enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: "error" });
      return rejectWithValue(error);
    }
  },
);

export const getProductVariationThunk = createAsyncThunk(
  "productAnalyzer/getProductVariationThunk",
  async ({ asin }: { asin: string }, { rejectWithValue }) => {
    try {
      const response = await getProductVariations(asin);
      return response.data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error);
    }
  },
);

export const getAveragesThunk = createAsyncThunk(
  "productAnalyzer/getAverages",
  async (asin: string, { rejectWithValue }) => {
    try {
      const response = await getAverages(asin);

      if (isAsinNotFoundResponse(response.data)) {
        console.warn(
          `No updated data available for ASIN: ${asin}. Reason: ${ASIN_NOT_FOUND_ERR}`,
        );
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
       console.error(error);
      // enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: "error" });
      return rejectWithValue(error);
    }
  },
);

export const createProductPurchaseThunk = createAsyncThunk(
  "productAnalyzer/createProductPurchaseThunk",
  async (data: ICreateProductPurchaseRequest, { rejectWithValue }) => {
    try {
      const response = await createProductPurchase(data);
      return response.data;
    } catch (error) {
      console.error(error);
      // enqueueSnackbar("Something went wrong. Please try again later", {
      //   variant: "error",
      // });
      return rejectWithValue(error);
    }
  },
);

export const getProductHistoriesThunk = createAsyncThunk(
  "productAnalyzer/getProductHistories",
  async (req: IGetProductHistoriesRequest, { rejectWithValue }) => {
    try {
      const response = await getProductHistories(req);
      return response.data;
    } catch (error) {
      console.error(error);
      // enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: "error" });
      return rejectWithValue(error);
    }
  },
);

export const getProductTagsThunk = createAsyncThunk(
  "productAnalyzer/getProductTagsThunk",
  async (asin: string, { rejectWithValue }) => {
    try {
      const res = await getProductTags(asin);
      return res.data;
    } catch (e) {
      const error = e as AxiosError<TErrorResponse>;
      if (
        error.status === 404 &&
        error.response?.data?.ErrorMessage ===
        "Tags not exists for the product."
      ) {
        return { tags: [] };
      }
      console.error(error);
      // enqueueSnackbar(
      //   error.response?.data?.ErrorMessage ?? INTERNAL_SERVER_ERROR,
      //   { variant: "error" },
      // );
      return rejectWithValue(error);
    }
  },
);

export const createProductTagThunk = createAsyncThunk(
  "productAnalyzer/createProductTagThunk",
  async (req: ICreateProductTagRequest, { rejectWithValue }) => {
    try {
      const res = await createProductTag(req);
      return res.data;
    } catch (error) {
      console.error(error);
      // enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: "error" });
      return rejectWithValue(error);
    }
  },
);

export const deleteProductTagThunk = createAsyncThunk(
  "productAnalyzer/deleteProductTagThunk",
  async (req: IDeleteProductTagRequest, { rejectWithValue }) => {
    try {
      const res = await deleteProductTag(req);
      return res.data;
    } catch (error) {
      console.error(error);
      // enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: "error" });
      return rejectWithValue(error);
    }
  },
);

export const updateLookupThunk = createAsyncThunk(
  "productAnalyzer/updateLookupThunk",
  debounce(
    async (
      req: Omit<IUpdateLookupRequest, "id">,
      { getState, rejectWithValue },
    ) => {
      try {
        const state = getState() as RootState;
        const id = state.productAnalyzer.productDetail?.lookupId;
        const { profitCalculator, feeRules } = state.productAnalyzer;
        if (!id) throw new Error("Can not find lookup id.");
        const { profit, margin, roi, breakevenSalePrice } =
          new ProfitCalculator(
            { ...profitCalculator, ...req },
            feeRules,
          ).calculate();
        await updateLookup({
          id,
          ...profitCalculator,
          ...req,
          profit,
          profitMargin: margin,
          roi,
          breakeven: breakevenSalePrice,
        });
        return true;
      } catch (e) {
        const error = e as AxiosError<TErrorResponse>;
        console.error(error);
        // if (error.status === 400) {
        //   // Validation errors
        //   enqueueSnackbar(error.response?.data?.ErrorMessage, {
        //     variant: "warning",
        //     preventDuplicate: true,
        //   });
        // } else {
        //   enqueueSnackbar(Notifications.PA_UPDATE_ERROR, {
        //     variant: "error",
        //     preventDuplicate: true,
        //   });
        // }
        return rejectWithValue(error);
      }
    },
    UPDATE_DEBOUNCE_DELAY,
  ),
  {
    condition: ({ miscFeePercent }: Omit<IUpdateLookupRequest, "id">) => {
      // if (miscFeePercent && (miscFeePercent < 0 || miscFeePercent > 100)) {
      //   enqueueSnackbar(
      //     Notifications.MISC_FEE_PERCENT_BETWEEN_ZERO_TO_HUNDRED,
      //     {
      //       variant: "warning",
      //       preventDuplicate: true,
      //     },
      //   );
      //   return false;
      // }
      return true;
    },
  },
);

export const getLookupThunk = createAsyncThunk(
  "productAnalyzer/getLookupThunk",
  async (lookupId: string, { getState, rejectWithValue }) => {
    try {
      const res = await getLookup(lookupId);
      return res.data;
    } catch (e) {
      const error = e as AxiosError<TErrorResponse>;
      console.error(error);
      return rejectWithValue(error);
    }
  },
);

